import React, { useState, useEffect } from 'react';
import { Search, X, ChevronDown, ChevronRight } from 'lucide-react';
import api from '../../lib/api';

const CATEGORY_LABELS = {
  mecanique: 'Mécanique',
  univers: 'Univers',
  theme: 'Thème',
  gamme: 'Gamme',
  autre: 'Autre',
};
const CATEGORY_ORDER = ['mecanique', 'univers', 'theme', 'gamme', 'autre'];

/**
 * TagsProductFilter V2 — Avec facets compteurs + états désactivés (Phase 3.4.4)
 */
export default function TagsProductFilter({ selected = [], onChange, facets = {} }) {
  const [tags, setTags] = useState([]);
  const [search, setSearch] = useState('');
  const [openCats, setOpenCats] = useState({});

  useEffect(() => {
    api.get('/referentiels/tags_produit?include_inactive=false')
      .then(res => {
        const data = res.data;
        setTags(data?.items || (Array.isArray(data) ? data : []));
      })
      .catch(() => setTags([]));
  }, []);

  const grouped = {};
  tags.forEach(t => {
    const cat = t.category || 'autre';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(t);
  });

  const filteredGrouped = {};
  const searchLower = search.toLowerCase();
  Object.entries(grouped).forEach(([cat, items]) => {
    const filtered = searchLower ? items.filter(t => (t.label || t.value || '').toLowerCase().includes(searchLower)) : items;
    if (filtered.length > 0) filteredGrouped[cat] = filtered;
  });

  const toggleCat = (cat) => setOpenCats(prev => ({ ...prev, [cat]: !prev[cat] }));
  const toggleTag = (tagId) => {
    const count = facets[tagId]?.count;
    if (count === 0 && !selected.includes(tagId)) return; // Don't select 0-count tags
    const next = selected.includes(tagId) ? selected.filter(id => id !== tagId) : [...selected, tagId];
    onChange(next);
  };

  const sortedCats = CATEGORY_ORDER.filter(c => filteredGrouped[c]);

  // Category total from facets
  const getCatTotal = (cat) => {
    const items = filteredGrouped[cat] || [];
    let total = 0;
    items.forEach(t => {
      const f = facets[t.id];
      if (f && typeof f.count === 'number') total += f.count;
    });
    return total;
  };

  return (
    <div data-testid="tags-product-filter">
      <div className="relative mb-2">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        <input
          type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher un tag..."
          className="w-full pl-7 pr-7 py-1.5 text-xs border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-gray-400"
          data-testid="tags-filter-search"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      {sortedCats.map(cat => {
        const catTotal = getCatTotal(cat);
        return (
          <div key={cat} className="mb-1">
            <button
              onClick={() => toggleCat(cat)}
              className="flex items-center justify-between w-full py-1.5 px-1 text-xs font-semibold text-gray-600 hover:text-gray-900 rounded hover:bg-gray-50"
              data-testid={`tags-cat-${cat}`}
            >
              <span>
                {CATEGORY_LABELS[cat] || cat}
                <span className="ml-1 font-normal text-gray-400">({catTotal})</span>
              </span>
              {openCats[cat] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
            {openCats[cat] && (
              <div className="ml-1 space-y-0.5">
                {filteredGrouped[cat].map(tag => {
                  const tagFacet = facets[tag.id];
                  const count = tagFacet?.count ?? null;
                  const isZero = count === 0;
                  return (
                    <label
                      key={tag.id}
                      className={`flex items-center gap-2 py-0.5 px-1 text-xs rounded transition-colors ${
                        isZero ? 'text-gray-300 cursor-default' : 'text-gray-700 hover:bg-gray-50 cursor-pointer'
                      }`}
                      data-testid={`tag-filter-${tag.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={selected.includes(tag.id)}
                        onChange={() => toggleTag(tag.id)}
                        disabled={isZero}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-gray-800 focus:ring-gray-400 disabled:opacity-40"
                      />
                      <span className={isZero ? 'line-through' : ''}>{tag.label || tag.value}</span>
                      {count !== null && (
                        <span className={`text-[10px] ml-auto tabular-nums ${isZero ? 'text-gray-300' : 'text-gray-400'}`}>
                          ({count})
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {sortedCats.length === 0 && search && (
        <p className="text-xs text-gray-400 italic px-1">Aucun tag trouvé</p>
      )}
    </div>
  );
}
